import { Check, CornerDownLeft, Play } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import type { SUPPORTED_LOCALES } from '@/i18n/config';
import { useLocale } from '@/i18n/useLocale';
import { useTheme } from '@/theme/ThemeProvider';
import { useDensity } from '../density/DensityProvider';
import type { ResolvedAuxiliaryScreen, ResolvedModule } from '../model/resolve-navigation';
import { buildCommandActions } from './command-actions';
import { buildCommandDestinations } from './command-destinations';
import {
  actionItems,
  entityItems,
  filterCommandItems,
  navigationItems,
  type CommandItem,
} from './command-items';
import { useCommandEntities } from './use-command-entities';
import { useAdminCommandItems } from '@/admin-area/command/use-admin-command-items';

/** The palette's actions: theme, density, language and sign-out, each closing the palette first. */
function useActionRows(onClose: () => void) {
  const { t } = useTranslation('shell');
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const { density, setDensity } = useDensity();
  const { locale, setLocale } = useLocale();
  const { signOutCurrentUser } = useAuth();
  return useMemo(
    () =>
      actionItems(
        buildCommandActions(
          {
            themeMode,
            setThemeMode,
            density,
            setDensity,
            locale,
            setLocale,
            signOut: () => void signOutCurrentUser(),
          },
          {
            theme: {
              light: t('command.actionTheme', { value: t('theme.light') }),
              dark: t('command.actionTheme', { value: t('theme.dark') }),
              system: t('command.actionTheme', { value: t('theme.system') }),
            },
            density: {
              comfortable: t('command.actionDensity', { value: t('density.comfortable') }),
              compact: t('command.actionDensity', { value: t('density.compact') }),
            },
            language: {
              // The autonym, so a reader can find their language whatever the UI is currently in —
              // the same rule `LanguageSwitcher` follows, and for the same reason.
              it: t('command.actionLanguage', { value: autonym('it') }),
              en: t('command.actionLanguage', { value: autonym('en') }),
            },
            signOut: t('command.actionSignOut'),
          },
        ),
        t('command.groupActions'),
      ).map((item) => ({
        ...item,
        // Every action closes the palette first, then acts: several of them re-render the whole
        // shell, and doing that with a dialog still open fights the focus trap.
        perform: () => {
          onClose();
          item.perform();
        },
      })),
    [
      themeMode,
      setThemeMode,
      density,
      setDensity,
      locale,
      setLocale,
      signOutCurrentUser,
      t,
      onClose,
    ],
  );
}

function usePaletteRows({
  open,
  query,
  organizationId,
  modules,
  auxiliaryScreens,
  onClose,
}: {
  readonly open: boolean;
  readonly query: string;
  readonly organizationId: string | undefined;
  readonly modules: readonly ResolvedModule[];
  readonly auxiliaryScreens: readonly ResolvedAuxiliaryScreen[];
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const { loading: entitiesLoading, entities } = useCommandEntities(open, organizationId);
  // Inside the administration area: the place's own acts, and its people once something is typed.
  const goFromPalette = useCallback(
    (path: string) => {
      onClose();
      void navigate(path);
    },
    [onClose, navigate],
  );
  const { items: adminRows, loading: adminLoading } = useAdminCommandItems(
    open,
    query,
    goFromPalette,
  );

  const navigationRows = useMemo(
    () =>
      navigationItems(
        buildCommandDestinations(modules, auxiliaryScreens),
        t('command.groupNavigation'),
        (path) => {
          onClose();
          void navigate(path);
        },
      ),
    [modules, auxiliaryScreens, t, navigate, onClose],
  );

  const actionRows = useActionRows(onClose);

  const entityRows = useMemo(
    () =>
      entityItems(entities, t('command.groupStudies'), (path) => {
        onClose();
        void navigate(path);
      }),
    [entities, t, navigate, onClose],
  );
  return { navigationRows, actionRows, entityRows, adminRows, entitiesLoading, adminLoading };
}

function PaletteList({
  listId,
  listRef,
  matches,
  loading,
  query,
  optionIdPrefix,
  boundedIndex,
  onActivate,
}: {
  readonly listId: string;
  readonly listRef: RefObject<HTMLUListElement | null>;
  readonly matches: readonly CommandItem[];
  readonly loading: boolean;
  readonly query: string;
  readonly optionIdPrefix: string;
  readonly boundedIndex: number;
  readonly onActivate: (index: number) => void;
}) {
  const { t } = useTranslation('shell');
  return (
    <ul
      className="max-h-[46vh] overflow-y-auto p-1.5"
      id={listId}
      ref={listRef}
      role="listbox"
      aria-label={t('command.resultsLabel')}
    >
      {matches.length === 0 ? (
        <li className="text-muted-foreground px-2.5 py-6 text-center text-sm">
          {loading ? t('command.searching') : t('command.noResults', { query: query.trim() })}
        </li>
      ) : (
        matches.map((item, index) => (
          <Fragment key={item.id}>
            {index === 0 || matches[index - 1]?.group !== item.group ? (
              <li
                aria-hidden="true"
                className="text-muted-foreground px-2.5 pt-2.5 pb-1 text-[11px] leading-[14px]
                      font-semibold tracking-[0.08em] uppercase"
              >
                {item.group}
              </li>
            ) : null}
            <CommandRow
              item={item}
              id={`${optionIdPrefix}-${index}`}
              active={index === boundedIndex}
              onActivate={() => onActivate(index)}
            />
          </Fragment>
        ))
      )}
    </ul>
  );
}

/** Keeps the active row in view when the arrows walk past the panel's scroll edge. */
function useScrollActiveIntoView(
  listRef: RefObject<HTMLUListElement | null>,
  optionIdPrefix: string,
  boundedIndex: number,
  matchCount: number,
) {
  useEffect(() => {
    const list = listRef.current;
    if (list === null) {
      return;
    }
    const activeRow = list.querySelector(`#${CSS.escape(`${optionIdPrefix}-${boundedIndex}`)}`);
    // `scrollIntoView` is optional in the DOM: jsdom does not implement it at all, and neither do
    // some embedded webviews. Calling it unguarded threw inside a render effect and took the whole
    // palette down with it — a keyboard nicety must never be able to do that.
    if (activeRow instanceof HTMLElement && typeof activeRow.scrollIntoView === 'function') {
      activeRow.scrollIntoView({ block: 'nearest' });
    }
  }, [boundedIndex, matchCount, optionIdPrefix, listRef]);
}

function PaletteInput({
  query,
  listId,
  activeId,
  onQuery,
  onKeyDown,
}: {
  readonly query: string;
  readonly listId: string;
  readonly activeId: string | undefined;
  readonly onQuery: (value: string) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation('shell');
  return (
    <Input
      autoFocus
      type="text"
      role="combobox"
      aria-expanded
      aria-controls={listId}
      aria-label={t('command.inputLabel')}
      {...(activeId === undefined ? {} : { 'aria-activedescendant': activeId })}
      placeholder={t('command.placeholder')}
      value={query}
      onChange={(event) => onQuery(event.target.value)}
      onKeyDown={onKeyDown}
    />
  );
}

function handlePaletteKey(
  event: KeyboardEvent<HTMLInputElement>,
  matches: readonly CommandItem[],
  activeItem: CommandItem | undefined,
  setActiveIndex: Dispatch<SetStateAction<number>>,
) {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    setActiveIndex((current) =>
      matches.length === 0 ? 0 : (Math.min(current, matches.length - 1) + 1) % matches.length,
    );
    return;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    setActiveIndex((current) =>
      matches.length === 0
        ? 0
        : (Math.min(current, matches.length - 1) + matches.length - 1) % matches.length,
    );
    return;
  }
  if (event.key === 'Enter' && activeItem !== undefined) {
    event.preventDefault();
    activeItem.perform();
  }
  // `Escape` is deliberately not handled here — the dialog primitive owns it, and a
  // second handler would close the palette twice and fight over focus restoration.
}

/**
 * The command palette the standard requires on `⌘K` / `Ctrl+K`: it "navigates, finds entities, runs
 * actions", and now does all three.
 *
 * THE THREE SOURCES ARE NORMALISED BEFORE THEY GET HERE (`command-items.ts`), so this component
 * holds one list, one filter and one keyboard model regardless of how many kinds of row exist. Each
 * source keeps its own file and its own rules — navigation derives from the SAME gated model the
 * rail renders, actions are the shell's own display and session controls, entities are the study
 * list the server has already confined to this caller.
 *
 * WHY IT REUSES `Sheet`. `Sheet` is this codebase's Radix-dialog primitive and it already carries
 * the four things a palette must not get wrong: a portal, a scrim, a focus trap, and `Escape` to
 * close. `side="top"` plus a max width and a top margin places it as a panel dropping from the top
 * edge rather than a full-bleed bar.
 *
 * THE KEYBOARD MODEL is `combobox` + `listbox` with `aria-activedescendant`: focus STAYS in the
 * input while the arrows move the active option, which is what lets a person keep typing to narrow
 * after moving. Moving DOM focus onto the options instead is the common mistake — it works with a
 * mouse and strands a keyboard user, who then has to Shift+Tab back to refine the query.
 *
 * GROUP HEADINGS ARE PRESENTATIONAL. The options stay a FLAT sequence of `role="option"` children,
 * because `aria-activedescendant` addresses one option at a time and the arrow keys walk the whole
 * result set irrespective of grouping. Marking the headings up as real `role="group"` wrappers
 * would nest the options one level deeper for no behavioural gain, and every row that needs its
 * context spoken already carries it in its own accessible name.
 */
export function CommandPalette({
  open,
  onClose,
  modules,
  auxiliaryScreens,
  organizationId,
}: {
  open: boolean;
  onClose: () => void;
  modules: readonly ResolvedModule[];
  auxiliaryScreens: readonly ResolvedAuxiliaryScreen[];
  /** Scopes the entity search, and discards its cache when the active organization changes. */
  organizationId?: string | undefined;
}) {
  const { t } = useTranslation('shell');
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const optionIdPrefix = useId();
  const listRef = useRef<HTMLUListElement | null>(null);
  const rows = usePaletteRows({ open, query, organizationId, modules, auxiliaryScreens, onClose });
  const { navigationRows, actionRows, entityRows, adminRows } = rows;

  const hasQuery = query.trim().length > 0;
  const matches = useMemo(() => {
    // An unfiltered palette opens on where you can go and what you can do. The entity rows join in
    // only once there is something to match them against — otherwise the first thing a person sees
    // on `⌘K` is a list of every study in the organization, which buries the navigation the
    // shortcut is most often pressed for.
    const rows = hasQuery
      ? [...navigationRows, ...entityRows, ...adminRows, ...actionRows]
      : [...navigationRows, ...adminRows, ...actionRows];
    return filterCommandItems(rows, query);
  }, [hasQuery, navigationRows, entityRows, adminRows, actionRows, query]);

  // A fresh palette on every open: a stale query from three screens ago is never what someone
  // pressing ⌘K wants to see, and re-mounting is not an option — `Sheet` keeps it mounted.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  // The active row must exist. Narrowing from twelve matches to two while the active index sits at
  // 7 would otherwise leave `aria-activedescendant` pointing at nothing and Enter doing nothing.
  const boundedIndex = matches.length === 0 ? 0 : Math.min(activeIndex, matches.length - 1);
  const activeItem = matches[boundedIndex];

  useScrollActiveIntoView(listRef, optionIdPrefix, boundedIndex, matches.length);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <SheetContent
        side="top"
        className="border-border bg-card mx-auto mt-[8vh] max-w-[600px] gap-0 overflow-hidden
          rounded-lg border p-0 shadow-[var(--shadow-3)]"
        aria-label={t('command.title')}
      >
        <SheetTitle className="sr-only">{t('command.title')}</SheetTitle>
        <div className="border-border border-b p-2.5">
          <PaletteInput
            query={query}
            listId={listId}
            activeId={activeItem === undefined ? undefined : `${optionIdPrefix}-${boundedIndex}`}
            onQuery={(value) => {
              setQuery(value);
              setActiveIndex(0);
            }}
            onKeyDown={(event) => handlePaletteKey(event, matches, activeItem, setActiveIndex)}
          />
        </div>
        <PaletteList
          listId={listId}
          listRef={listRef}
          matches={matches}
          loading={rows.entitiesLoading || rows.adminLoading}
          query={query}
          optionIdPrefix={optionIdPrefix}
          boundedIndex={boundedIndex}
          onActivate={setActiveIndex}
        />
      </SheetContent>
    </Sheet>
  );
}

function ActiveMark({ kind }: { readonly kind: CommandItem['kind'] }) {
  const Icon = kind === 'run' ? Play : CornerDownLeft;
  return <Icon aria-hidden="true" className="text-muted-foreground size-3.5" />;
}

function CommandRow({
  item,
  id,
  active,
  onActivate,
}: {
  item: CommandItem;
  id: string;
  active: boolean;
  onActivate: () => void;
}) {
  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      // The option under the keyboard is SELECTED, so it takes the selection blue (Brand Kit
      // § Neutrals, text and blue) rather than the grey of a hovered control.
      className={`flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-md px-2.5
        text-[14.5px] ${active ? 'bg-selected text-selected-foreground font-semibold' : ''}`}
      // `onMouseDown`, not `onClick`: the input holds focus and a click would blur it first, which
      // in a focus-trapped dialog re-runs the trap's own focus logic before the action lands.
      onMouseDown={(event) => {
        event.preventDefault();
        item.perform();
      }}
      onMouseEnter={onActivate}
    >
      {item.current === true ? <Check aria-hidden="true" className="size-3.5 shrink-0" /> : null}
      <span className="min-w-0 flex-1 truncate">
        {item.label}
        {item.hint === undefined ? null : (
          <span className="text-muted-foreground text-[12px] font-medium">
            {' · '}
            {item.hint}
          </span>
        )}
      </span>
      {active ? <ActiveMark kind={item.kind} /> : null}
    </li>
  );
}

/** Each language in its OWN name, so it is findable whatever the UI language is. */
function autonym(locale: (typeof SUPPORTED_LOCALES)[number]): string {
  const name = new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale;
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
}
